import { Tab, Tabs } from '@material-ui/core'
import { assets, PolicyDispatchingPermission } from '@pulseops/common'
import { PDStockOutPostOfficeHandle } from './components/PDStockOutPostOffice'
import React from 'react'
import { View, Text } from 'react-native'
import { StackScreenProps } from '@react-navigation/stack'
import { PolicyDispatchingStackParamList, StockOutTabType } from './PolicyDispatchingStackParamList'
import PDStockOutAgent, { PDStockOutAgentHandle } from './components/PDStockOutAgent'
import PDStockOutAgentGroup, { PDStockOutAgentGroupHandle } from './components/PDStockOutAgentGroup'
import PDStockOutPostOffice from './components/PDStockOutPostOffice'
import { getPermissions } from './utils/policyDispatchingUtils'

const tabIndexMap: Record<StockOutTabType, number> = {
  agent: 0,
  agentGroup: 1,
  postOffice: 2
}

const indexTabMap: Record<number, StockOutTabType> = {
  0: 'agent',
  1: 'agentGroup',
  2: 'postOffice'
}

type Props = StackScreenProps<PolicyDispatchingStackParamList, 'PDStockOutScreen'>

export default function PDStockOutScreen({ route, navigation }: Props) {
  const tab = route.params?.tab
  const [value, setValue] = React.useState(tab ? tabIndexMap[tab] : 0)
  const agentRef = React.useRef<PDStockOutAgentHandle>(null)
  const agentGroupRef = React.useRef<PDStockOutAgentGroupHandle>(null)
  const postOfficeRef = React.useRef<PDStockOutPostOfficeHandle>(null)
  const permissions = getPermissions()
  const allowDelivery = permissions.includes(PolicyDispatchingPermission.StockOutDeliverContract)
  const allowCancelDelivery = permissions.includes(PolicyDispatchingPermission.StockOutCancelDelivery)
  const allowChangeDate = permissions.includes(PolicyDispatchingPermission.StockOutAdjustDelivery)

  React.useEffect(() => {
    if (tab) {
      setValue(tabIndexMap[tab])
    }
  }, [tab])

  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setValue(newValue)
    navigation.setParams({ tab: indexTabMap[newValue] })
  }

  const a11yProps = (index: number) => {
    return {
      id: `stock-out-tab-${index}`,
      'aria-controls': `stock-out-tabpanel-${index}`
    }
  }

  const getContent = () => {
    switch (value) {
      case 0:
        return <PDStockOutAgent ref={agentRef} styles={styles} />
      case 1:
        return <PDStockOutAgentGroup ref={agentGroupRef} styles={styles} />
      case 2:
        return <PDStockOutPostOffice ref={postOfficeRef} styles={styles} />
      default:
        return null
    }
  }

  return (
    <View style={styles.container}>
      <View>
        <Text style={styles.title}>
          Giao hợp đồng - {value === 0 ? 'Đại lý' : value === 1 ? 'Nhóm đại lý' : 'Bưu điện'}
        </Text>
      </View>
      <View style={styles.wrapper}>
        <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 32 } as any}>
          <View>
            <Text style={styles.subTitle}> - Giao hợp đồng qua</Text>
          </View>
          <View style={{ marginTop: 16 } as any}>
            <Tabs
              value={value}
              onChange={handleChange}
              aria-label="stock out tabs"
              TabIndicatorProps={{ style: { display: 'none' } }}
            >
              <Tab label="Đại lý" {...a11yProps(0)} style={value === 0 ? styles.selectedTab : styles.unselectedTab} />
              <Tab
                label="Nhóm đại lý"
                {...a11yProps(1)}
                style={value === 1 ? styles.selectedTab : styles.unselectedTab}
              />
              <Tab label="Bưu điện" {...a11yProps(2)} style={value === 2 ? styles.selectedTab : styles.unselectedTab} />
            </Tabs>
          </View>
        </View>
        {getContent()}
      </View>
      <View style={styles.bottomAction}>
        <button
          style={!allowDelivery ? styles.disabledButton : styles.submitButton}
          disabled={!allowDelivery}
          type="button"
          onClick={() => {
            if (value === 0) {
              agentRef.current?.handleDeliveryContracts()
            }
            if (value === 1) {
              agentGroupRef.current?.handleDeliveryContracts()
            }
            if (value === 2) {
              postOfficeRef.current?.handleDeliveryContracts()
            }
          }}
        >
          <span style={styles.mr_5}>
            <assets.PDDelivery />
          </span>
          Giao hợp đồng
        </button>
        <button
          style={!allowCancelDelivery ? styles.disabledButton : styles.button}
          disabled={!allowCancelDelivery}
          type="button"
          onClick={() => {
            if (value === 0) {
              agentRef.current?.handleCancelContracts()
            }
            if (value === 1) {
              agentGroupRef.current?.handleCancelContracts()
            }
            if (value === 2) {
              postOfficeRef.current?.handleCancelContracts()
            }
          }}
        >
          <span style={styles.mr_5}>
            <assets.PDCancel />
          </span>
          Hủy giao
        </button>
        <button
          style={!allowChangeDate ? styles.disabledButton : styles.button}
          disabled={!allowChangeDate}
          type="button"
          onClick={() => {
            if (value === 0) {
              agentRef.current?.handleAdjustDeliveryDate()
            }
            if (value === 1) {
              agentGroupRef.current?.handleAdjustDeliveryDate()
            }
            if (value === 2) {
              postOfficeRef.current?.handleAdjustDeliveryDate()
            }
          }}
        >
          <span style={styles.mr_5}>
            <assets.PDEntryDate />
          </span>
          Điều chỉnh ngày giao
        </button>
      </View>
    </View>
  )
}

const styles: Record<string, any> = {
  container: {
    paddingTop: 0,
    paddingRight: 40,
    paddingBottom: 30,
    paddingLeft: 40,
    height: '100%',
    overflow: 'auto'
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    marginBottom: '20px'
  },
  subTitle: {
    fontSize: '18px',
    fontWeight: '700',
    whiteSpace: 'nowrap' as const
  },
  wrapper: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '20px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    position: 'relative' as const
  },
  selectedTab: {
    backgroundColor: '#ED1B2E',
    paddingTop: 4,
    paddingRight: 20,
    paddingBottom: 4,
    paddingLeft: 20,
    color: '#fff',
    fontWeight: 'bold',
    borderRadius: '10px',
    minWidth: 'auto',
    minHeight: 'auto',
    textTransform: 'none' as const
  },
  unselectedTab: {
    paddingTop: 4,
    paddingRight: 20,
    paddingBottom: 4,
    paddingLeft: 20,
    color: '#000',
    minWidth: 'auto',
    minHeight: 'auto',
    textTransform: 'none' as const
  },
  submitButton: {
    backgroundColor: '#ED1B2E',
    color: '#fff',
    borderWidth: 0,
    paddingTop: 8,
    paddingRight: 16,
    paddingBottom: 8,
    paddingLeft: 16,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    fontWeight: '600'
  },
  mr_5: {
    marginRight: '5px'
  },
  button: {
    backgroundColor: '#fff',
    color: '#000',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#d1d5dc',
    paddingTop: 8,
    paddingRight: 16,
    paddingBottom: 8,
    paddingLeft: 16,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    fontWeight: '600'
  },
  disabledButton: {
    backgroundColor: '#e5e7eb',
    color: '#9ca3af',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#d1d5db',
    paddingTop: 8,
    paddingRight: 16,
    paddingBottom: 8,
    paddingLeft: 16,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    fontWeight: '600',
    cursor: 'not-allowed',
    opacity: 0.7
  },
  bottomAction: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 32
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 24,
    width: '100%'
  },
  column_1_4: {
    width: 'calc(100% / 4 - 18px)'
  },
  column_1_2: {
    width: 'calc(100% / 2 - 12px)'
  },
  loadingOverlay: {
    position: 'fixed' as const,
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    zIndex: 2
  }
}
